import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const waarden = [
  { icon: 'ri-shield-check-line', title: 'Transparantie', desc: 'Geen verborgen kosten, geen dubbele agenda. Wij communiceren open over prijzen, marges en processen.' },
  { icon: 'ri-handshake-line', title: 'Partnerschap', desc: 'Wij zijn geen tussenpersoon die snel wil scoren — we bouwen langdurige relaties en denken mee als jouw inkooppartner.' },
  { icon: 'ri-medal-2-line', title: 'Kwaliteit eerst', desc: 'Goedkoop betekent niet slecht. Wij selecteren alleen fabrieken die kwaliteit leveren die Europese normen haalt.' },
  { icon: 'ri-time-line', title: 'Snelheid', desc: 'Binnen 24 uur een prijsindicatie. Wij weten dat ondernemers geen tijd hebben om weken te wachten op een offerte.' },
];

const tijdlijn = [
  {
    jaar: 'Begin',
    icon: 'ri-lightbulb-line',
    titel: 'Inkopen uit China als hobby',
    tekst: 'Het begon puur uit interesse. Zelf producten ontdekken bij Chinese fabrikanten, prijsverschillen begrijpen, zien hoe de handelswereld écht werkt. Wat als hobby startte, werd al snel een passie.',
  },
  {
    jaar: 'Vrienden',
    icon: 'ri-group-line',
    titel: 'Vrienden & familie helpen',
    tekst: 'Al gauw kwamen vrienden en kennissen met vragen: "Kun jij dat ook voor mij regelen?" Bedrukte tassen voor een zaak, cadeautasjes voor een juwelier, merchandise voor een event. De vraag groeide vanzelf.',
  },
  {
    jaar: 'Orders',
    icon: 'ri-shopping-cart-line',
    titel: 'Eerste serieuze bestellingen',
    tekst: 'Van kleine gunsten naar echte opdrachten. Bedrijven kwamen via via, grotere volumes, hogere verwachtingen — en DUC leverde. Het vertrouwen groeide per succesvolle order.',
  },
  {
    jaar: 'Netwerk',
    icon: 'ri-building-2-line',
    titel: 'Netwerk in China opgebouwd',
    tekst: 'Jaar na jaar zijn directe contacten bij fabrieken in Guangdong, Zhejiang en Fujian opgebouwd. Geen Alibaba-tussenhandel — echte fabrieksprijzen via persoonlijke relaties.',
  },
  {
    jaar: '2025',
    icon: 'ri-rocket-line',
    titel: 'DUC officieel opgericht',
    tekst: 'Dit jaar de stap gezet: wat jarenlang informeel werkte, is nu een officieel bedrijf. Direct Uit China — omdat de naam zegt wat wij doen. Transparant, eerlijk, direct.',
  },
  {
    jaar: 'Nu',
    icon: 'ri-star-line',
    titel: 'Serieuze klanten, serieuze resultaten',
    tekst: 'Al meteen na de oprichting serieuze klanten: juweliers, eventbureaus, beautycenters, horecazaken. Elk met hun eigen product, elk met hetzelfde resultaat: aanzienlijk goedkoper inkopen via DUC.',
  },
];

const stats = [
  { value: '2025', label: 'Officieel opgericht', icon: 'ri-calendar-check-line' },
  { value: '100+', label: 'Tevreden klanten', icon: 'ri-user-smile-line' },
  { value: '30–80%', label: 'Besparing per order', icon: 'ri-money-euro-circle-line' },
  { value: '24u', label: 'Reactietijd offerte', icon: 'ri-time-line' },
  { value: '100%', label: 'Transparante prijzen', icon: 'ri-shield-check-line' },
  { value: 'DDP', label: 'All-in levering NL', icon: 'ri-truck-line' },
];

const sectoren = [
  'Horeca & Foodservice', 'Juweliers & Sieraden', 'Beauty & Wellness',
  'Events & Promotie', 'Webshops & E-commerce', 'Retail & Groothandel',
  'Bedrijven & B2B', 'Private Label Merken',
];

const klanten = [
  { naam: 'Sehgal Juwelier', sector: 'Juweliers', icon: 'ri-vip-diamond-line' },
  { naam: 'Ekdo Events', sector: 'Events', icon: 'ri-megaphone-line' },
  { naam: 'Kamal Beauty Center', sector: 'Beauty', icon: 'ri-scissors-cut-line' },
  { naam: 'Lagaetano', sector: 'Horeca', icon: 'ri-restaurant-line' },
  { naam: '55 Bombay Street', sector: 'Retail', icon: 'ri-shopping-bag-line' },
  { naam: 'Munisha Beauty', sector: 'Beauty', icon: 'ri-medicine-bottle-line' },
  { naam: 'Holy Cow Dairy', sector: 'Food', icon: 'ri-leaf-line' },
];

export default function WieZijnWijPage() {
  useSEO({
    title: 'Wie zijn wij | Het verhaal van DUC - Direct Uit China | Sourcing Partner',
    description: 'Van hobby-inkoper naar professionele sourcing partner. Lees het verhaal van DUC: eerlijk, transparant inkopen uit China voor Nederlandse ondernemers. Opgericht in 2025.',
    keywords: 'DUC Direct Uit China, wie zijn wij, sourcing partner Nederland, betrouwbaar inkopen uit China, verhaal DUC',
    canonical: `${SITE_URL}/wie-zijn-wij`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Wie zijn wij', item: `${SITE_URL}/wie-zijn-wij` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'Wie zijn wij — Het verhaal van Direct Uit China (DUC)',
        description: 'Van hobby-inkoper naar professionele sourcing partner. Lees het verhaal van DUC: eerlijk, transparant inkopen uit China voor Nederlandse ondernemers.',
        url: `${SITE_URL}/wie-zijn-wij`,
        inLanguage: 'nl-NL',
        about: {
          '@type': 'Organization',
          name: 'Direct Uit China (DUC)',
          url: SITE_URL,
          foundingDate: '2025',
          foundingLocation: {
            '@type': 'Place',
            addressCountry: 'NL',
          },
          description: 'DUC is een Nederlandse sourcing agent opgericht in 2025. Wij helpen bedrijven en particulieren goedkoper inkopen uit China via directe fabrieksinkopers.',
          numberOfEmployees: {
            '@type': 'QuantitativeValue',
            value: 1,
          },
        },
      },
    ],
  });

  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });
  const verhaalRef = useRef<HTMLDivElement>(null);
  const verhaalInView = useInView(verhaalRef, { once: true, margin: '-80px' });
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineInView = useInView(timelineRef, { once: true, margin: '-80px' });
  const statsRef = useRef<HTMLDivElement>(null);
  const statsInView = useInView(statsRef, { once: true, margin: '-80px' });
  const waardenRef = useRef<HTMLDivElement>(null);
  const waardenInView = useInView(waardenRef, { once: true, margin: '-80px' });
  const klantenRef = useRef<HTMLDivElement>(null);
  const klantenInView = useInView(klantenRef, { once: true, margin: '-80px' });

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8e27373afadfa4501795004ba826df83.jpg"
            alt="DUC - Het verhaal van Direct uit China"
            className="w-full h-full object-cover object-top opacity-12"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/90 to-[#0A1628]/65" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 w-full">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-[#3B9EE8] text-sm mb-8 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line" /> Terug naar home
            </Link>
          </motion.div>
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6"
          >
            <i className="ri-heart-line" /> Ons verhaal
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="font-heading font-800 text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6"
          >
            Van hobby naar<br />
            <span className="text-[#3B9EE8]">serieus bedrijf</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="text-white/65 text-lg leading-relaxed max-w-2xl"
          >
            DUC begon niet in een boardroom. Het begon met een simpele vraag: waarom betalen Nederlandse ondernemers zoveel meer dan nodig is? Dat is het verhaal van Direct Uit China.
          </motion.p>
        </div>
      </section>

      {/* Het echte verhaal */}
      <section ref={verhaalRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <motion.span
                initial={{ opacity: 0, y: 12 }}
                animate={verhaalInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6"
              >
                <i className="ri-user-line" /> Shubam, oprichter DUC
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 16 }}
                animate={verhaalInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl leading-tight mb-6"
              >
                Het begon als<br />
                <span className="text-[#3B9EE8]">een hobby</span>
              </motion.h2>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={verhaalInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-5 text-[#0A1628]/65 text-base leading-relaxed"
              >
                <p>
                  Ik begon met inkopen uit China uit pure nieuwsgierigheid. Hoe werkt het? Wie zijn die fabrikanten? Hoe groot is het prijsverschil écht? Wat begon als hobby, werd al snel een obsessie — in de goede zin van het woord.
                </p>
                <p>
                  Al gauw kwamen vrienden en kennissen met verzoeken. Een juwelier die cadeautasjes op maat wilde. Een eventbureau dat promotiemateriaal zocht. Een horecaondernemer die zijn servetjes met logo wilde laten drukken. Ik hielp ze allemaal — en het werkte.
                </p>
                <p>
                  De resultaten spraken voor zich. Mensen bespaarden 40, 50, soms 70% op producten die ze al jarenlang via dure tussenleveranciers inkochten. <strong className="text-[#0A1628]">Dat voelde niet eerlijk — en ik kon het veranderen.</strong>
                </p>
                <p>
                  Na meerdere serieuze orders, tevreden klanten en een stevig netwerk van Chinese fabrieken was de keuze duidelijk: dit verdient een echt bedrijf. In 2025 is DUC officieel opgericht. Niet als groot corporation, maar als persoonlijke inkooppartner voor ondernemers die slimmer willen werken.
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={verhaalInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-4"
            >
              {/* Quote */}
              <div className="bg-[#0A1628] rounded-2xl p-8 text-white">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/15 mb-5">
                  <i className="ri-double-quotes-l text-[#3B9EE8] text-xl" />
                </div>
                <blockquote className="text-white/80 text-lg leading-relaxed mb-5 italic">
                  &ldquo;Ik zag dat grote bedrijven al jaren via China inkopen en daardoor een enorm voordeel hebben. Dat voordeel wil ik toegankelijk maken voor elke ondernemer — groot of klein.&rdquo;
                </blockquote>
                <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                  <div className="w-11 h-11 rounded-full bg-[#3B9EE8]/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-3-line text-[#3B9EE8] text-lg" />
                  </div>
                  <div>
                    <div className="text-white font-700 text-base">Shubam</div>
                    <div className="text-white/45 text-sm">Oprichter — Direct Uit China (DUC)</div>
                  </div>
                </div>
              </div>

              {/* 3 punten */}
              <div className="grid grid-cols-1 gap-3">
                {[
                  { icon: 'ri-lightbulb-line', title: 'Begonnen uit passie', desc: 'Geen investeerders, geen businessplan — gewoon echte kennis van hoe China-handel werkt.' },
                  { icon: 'ri-group-line', title: 'Gebouwd op vertrouwen', desc: 'Elk klant is via via gekomen. Mond-tot-mondreclame is ons sterkste bewijs.' },
                  { icon: 'ri-rocket-2-line', title: 'Gestart in 2025', desc: 'Officieel opgericht dit jaar. Geen opgeblazen claims — gewoon eerlijk en snel leveren.' },
                ].map((item) => (
                  <div key={item.title} className="bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl p-5 flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12 flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#3B9EE8] text-base`} />
                    </div>
                    <div>
                      <div className="font-700 text-[#0A1628] text-sm mb-1">{item.title}</div>
                      <div className="text-[#0A1628]/55 text-xs leading-relaxed">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tijdlijn */}
      <section ref={timelineRef} className="py-20 bg-[#F7F8FA]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={timelineInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6"
            >
              <i className="ri-history-line" /> Hoe het ging
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              animate={timelineInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl"
            >
              De weg naar <span className="text-[#3B9EE8]">DUC</span>
            </motion.h2>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-[#3B9EE8]/20 -translate-x-1/2 hidden md:block" />

            <div className="space-y-8">
              {tijdlijn.map((item, i) => {
                const isEven = i % 2 === 0;
                return (
                  <motion.div
                    key={item.jaar}
                    initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                    animate={timelineInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className={`flex items-start gap-4 md:gap-0 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    {/* Card */}
                    <div className={`flex-1 ${isEven ? 'md:pr-12' : 'md:pl-12'}`}>
                      <div className="bg-white border border-[#0A1628]/6 rounded-xl p-6 hover:border-[#3B9EE8]/30 transition-colors">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12 flex-shrink-0">
                            <i className={`${item.icon} text-[#3B9EE8] text-sm`} />
                          </div>
                          <span className="text-[#3B9EE8] text-xs font-700 uppercase tracking-wide">{item.jaar}</span>
                        </div>
                        <h3 className="font-heading font-700 text-[#0A1628] text-base mb-2">{item.titel}</h3>
                        <p className="text-[#0A1628]/60 text-sm leading-relaxed">{item.tekst}</p>
                      </div>
                    </div>

                    {/* Center dot */}
                    <div className="hidden md:flex items-center justify-center w-12 flex-shrink-0 mt-6">
                      <div className="w-4 h-4 rounded-full bg-[#3B9EE8] border-4 border-white" style={{ boxShadow: '0 0 0 2px #3B9EE8' }} />
                    </div>

                    {/* Spacer for alternating layout */}
                    <div className="hidden md:block flex-1" />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                animate={statsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl p-6 text-center"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12 mx-auto mb-3">
                  <i className={`${s.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <div className="font-heading font-800 text-[#0A1628] text-xl mb-1">{s.value}</div>
                <div className="text-[#0A1628]/50 text-xs leading-snug">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eerste klanten */}
      <section ref={klantenRef} className="py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={klantenInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6"
            >
              <i className="ri-star-line" /> Al direct serieuze klanten
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              animate={klantenInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-3"
            >
              Zij vertrouwen op DUC
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={klantenInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="text-[#0A1628]/55 text-base max-w-lg mx-auto"
            >
              Direct na de officiële oprichting kwamen de eerste serieuze klanten — via via, op basis van eerder geleverd werk en vertrouwen.
            </motion.p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {klanten.map((k, i) => (
              <motion.div
                key={k.naam}
                initial={{ opacity: 0, y: 20 }}
                animate={klantenInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="bg-white border border-[#0A1628]/6 rounded-xl p-5 text-center hover:border-[#3B9EE8]/30 transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/10 mx-auto mb-3">
                  <i className={`${k.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <div className="font-700 text-[#0A1628] text-xs leading-snug mb-1">{k.naam}</div>
                <div className="text-[#0A1628]/40 text-xs">{k.sector}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Kernwaarden */}
      <section ref={waardenRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={waardenInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6"
            >
              <i className="ri-compass-3-line" /> Onze kernwaarden
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              animate={waardenInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl"
            >
              Waar wij voor staan
            </motion.h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {waarden.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 20 }}
                animate={waardenInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl p-7"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 mb-5">
                  <i className={`${w.icon} text-[#3B9EE8] text-xl`} />
                </div>
                <h3 className="font-heading font-700 text-[#0A1628] text-base mb-2">{w.title}</h3>
                <p className="text-[#0A1628]/60 text-sm leading-relaxed">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectoren */}
      <section className="py-16 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-heading font-800 text-[#0A1628] text-2xl md:text-3xl mb-3">
            Wij werken voor ondernemers in <span className="text-[#3B9EE8]">alle sectoren</span>
          </h2>
          <p className="text-[#0A1628]/55 text-base mb-8 max-w-xl mx-auto">
            Van horecaondernemer tot juwelier, van webshop tot groothandel — iedereen kan profiteren van goedkoper inkopen via DUC.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {sectoren.map((s) => (
              <span key={s} className="bg-white border border-[#0A1628]/8 rounded-full px-5 py-2.5 text-[#0A1628]/70 text-sm font-500">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
            Word ook een <span className="text-[#3B9EE8]">DUC klant</span>
          </h2>
          <p className="text-white/55 text-base mb-8 leading-relaxed max-w-xl mx-auto">
            Vraag een gratis en vrijblijvende prijsindicatie aan. Binnen 24 uur weet je hoeveel jij kunt besparen op jouw inkoop.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-8 py-4 rounded-lg font-700 transition-all cursor-pointer whitespace-nowrap">
              Vraag gratis offerte aan <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20heb%20jullie%20verhaal%20gelezen%20en%20wil%20graag%20meer%20weten."
              target="_blank" rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-7 py-4 rounded-lg font-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl" /> WhatsApp Shubam
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
